import {ShellClientService} from "../../shell-communication/shell-client.service";
import {Injectable} from '@angular/core';
import {SHELL_PROTOCOL_OPERATIONS_NS} from "../../shell-communication/operations/shell-protocol-namespace.const";
import {ProtocolQuery} from "../../service-model/protocol-query.interface";
import {ProtocolCommand} from "../../service-model/protocol-command.interface";
import {isValidNumber} from "../../utils";
import {ApgPortfolio} from "../../adjustment-pricing-grid/model/ApgPortfolio";

export interface SupportResistanceLevel {
    id?: number;
    price: number;
    levelType: 'Support' | 'Resistance';
    createdAt?: Date;
    symbol?: string;
}

export interface ZoneIndicatorConfig {
    consecutiveDaysThreshold: number;
}

export interface ZoneEditorData {
    levels: SupportResistanceLevel[];
    config: ZoneIndicatorConfig;
}

// models/zone-editor-row.model.ts
export interface ZoneEditorRow {
    sequenceNumber: number;
    level: SupportResistanceLevel;
    isNew?: boolean;
    isDeleted?: boolean;
    isModified?: boolean;
}

//#region CQRS
export class GetSupportResistanceLevels implements ProtocolQuery {
    constructor() {
    }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.GetSupportResistanceLevels`;
    }
}

export interface GetSupportResistanceLevelsReply {
    levels: SupportResistanceLevel[];
}

export class GetCurrentZoneConfig implements ProtocolQuery {
    constructor() {
    }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.GetCurrentZoneConfig`;
    }
}

export interface GetCurrentZoneConfigReply {
    config: ZoneIndicatorConfig;
}

export class GetCurrentZone implements ProtocolQuery {
    constructor(public readonly symbol: string, public  readonly templateId?: string) {
    }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.GetCurrentZone`;
    }
}

export interface GetCurrentZoneReply {
    price?: number;
    zone?: number;
}

export class SaveCurrentZoneIndicatorSettings implements ProtocolCommand {
    constructor(public readonly levels: SupportResistanceLevel[], public readonly config: ZoneIndicatorConfig) {
    }
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.SaveCurrentZoneIndicatorSettings`;
    }
}

//#endregion

// services/zone-indicator-api.interface.ts
export interface IZoneIndicatorApi {
    getLevels(symbol: string): Promise<SupportResistanceLevel[]>;

    getConfig(): Promise<ZoneIndicatorConfig>;

    saveSettings(settings: {levels: SupportResistanceLevel[], config: ZoneIndicatorConfig}): PromiseLike<void>;

    refresh(symbol: string): Promise<void>;
}

@Injectable({
    providedIn: 'root'
})
export abstract class ZoneIndicatorApiService implements IZoneIndicatorApi {

    abstract getLevels(symbol: string): Promise<SupportResistanceLevel[]>;

    abstract getConfig(): Promise<ZoneIndicatorConfig>;

    abstract saveSettings(settings: {levels: SupportResistanceLevel[], config: ZoneIndicatorConfig}): PromiseLike<void>;

    abstract refresh(symbol: string): Promise<void>;

    abstract getCurrentZone(templateId: string): Promise<number>;

    abstract getClosePrice(): Promise<number>;
}

@Injectable({
    providedIn: 'root'
})
export class ZoneIndicatorApiServiceImpl extends ZoneIndicatorApiService {

    constructor(private readonly _shellClient: ShellClientService) {
        super();
    }

    private currentZone: number;
    private closePrice: number;

    async getConfig(): Promise<ZoneIndicatorConfig> {
        const qry = new GetCurrentZoneConfig();

        const reply =
            await this._shellClient.processQuery<GetCurrentZoneConfigReply>(qry);

        return reply.config;
    }

    async getLevels(symbol: string): Promise<SupportResistanceLevel[]> {

        const qry = new GetSupportResistanceLevels();

        const reply =
            await this._shellClient.processQuery<GetSupportResistanceLevelsReply>(qry);

        return reply.levels;
    }

    saveSettings(settings: { levels: SupportResistanceLevel[]; config: ZoneIndicatorConfig; }): PromiseLike<void> {
        const qry = new SaveCurrentZoneIndicatorSettings(settings.levels, settings.config);
        return this._shellClient.processCommand(qry);
    }

    async refresh(symbol: string): Promise<void> {
        const qry = new GetCurrentZone('SPX');
        const reply =
            await this._shellClient.processQuery<GetCurrentZoneReply>(qry);
        this.currentZone = reply.zone;
        this.closePrice = reply.price;
    }

    async getCurrentZone(templateId: string): Promise<number> {

        const qry = new GetCurrentZone('SPX', templateId);
        const reply =
            await this._shellClient.processQuery<GetCurrentZoneReply>(qry);
        return reply.zone;
    }

    async getClosePrice() : Promise<number> {
        if (!isValidNumber(this.closePrice, true)) {
            await this.refresh('SPX');
        }

        return this.closePrice;
    }
}